import React, { useState, useMemo } from 'react';
import { Platform, Linking } from 'react-native';

import { copyToClipboard } from '@/common/utils/clipboard';

import {
  Text,
  Icon,
  Box,
  HStack,
  VStack,
  Image,
  Pressable,
  Progress,
  ProgressFilledTrack,
  Menu,
  MenuItem,
  MenuItemLabel,
} from '@gluestack-ui/themed';

import {
  FileVideo, SquareArrowOutUpRight, FileText, Play, Youtube, MoreVertical, Notebook,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { useSelectedTask } from '../../../common/hooks/student/useSelectedTask';
import { useModuleSelectionState } from '../../../common/context/AppStateStore';
import { useDialog } from '../../../common/context/DialogContext';
import { VideoModal } from './VideoPlayer/components/VideoModal';

const ResourceThumbnail = React.memo(({ resource, thumbnail }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(true);
  const imageSource = useMemo(() => (thumbnail ? { uri: thumbnail } : null), [thumbnail]);

  const iconMap = {
    video: FileVideo,
    document: FileText,
    jupyter_notebook: Notebook,
    link: SquareArrowOutUpRight,
  };

  const IconComponent = resource?.type === 'link' && typeof resource?.url === 'string'
    ? (() => {
      const url = resource.url.toLowerCase();
      if (url.includes('youtube.com') || url.includes('youtu.be')) return Youtube;
      return iconMap[resource.type] || FileText;
    })()
    : iconMap[resource.type] || FileText;

  if (imageSource && isImageLoaded) {
    return (
      <Box
        position="relative"
        w="$12"
        height="$12"
        overflow="hidden"
        flexShrink={0}
        borderRadius="$md"
        borderWidth={1}
        borderColor="$borderLight"
      >
        <Image
          source={imageSource}
          alt="Resource thumbnail"
          width="100%"
          height="100%"
          onLoad={() => setIsImageLoaded(true)}
          onError={() => setIsImageLoaded(false)}
        />
        {resource.type === 'video' && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0,0,0,0.4)"
            alignItems="center"
            justifyContent="center"
            zIndex={1}
          >
            <Icon as={Play} size="xs" color="$white" />
          </Box>
        )}
        {resource.type === 'video'
          && !!resource.progress?.progress?.watchedSeconds
          && !!resource.progress?.progress?.totalSeconds && (
            <Box position="absolute" left={2} right={2} bottom={2} zIndex={2}>
              <Progress
                size="xs"
                value={Math.min(
                  (resource.progress.progress.watchedSeconds / resource.progress.progress.totalSeconds) * 100,
                  100,
                )}
                w="100%"
                h={2}
              >
                <ProgressFilledTrack bg="$blue500" />
              </Progress>
            </Box>
        )}
      </Box>
    );
  }

  // Fallback to icon when no thumbnail or loading failed
  return (
    <Box
      width="$12"
      height="$12"
      borderRadius="$md"
      bg="$bgSubtle"
      borderWidth={1}
      borderColor="$borderLight"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={IconComponent} size="md" color="$textSubtle" />
    </Box>
  );
});

const ResourceItem = ({ resource, thumbnail }) => {
  const { t } = useTranslation();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { task, updateProgress } = useSelectedTask();
  const { showToastMessage } = useDialog();
  const { selectedModule } = useModuleSelectionState();
  const courseId = selectedModule?.courseId;
  const moduleId = selectedModule?.moduleId;

  const readOnly = !task || task?.state?.studentWorkflowState === 'graded';

  const handleResourcePress = async () => {
    if (resource.type === 'video') {
      if (resource.url) {
        setShowVideoModal(true);
      } else {
        showToastMessage('error', t('student.task.resourcePanel.errors.video-url-failed'));
      }
    } else {
      // For non-video resources, update progress first
      if (!readOnly && courseId && moduleId && task?.id) {
        const progressData = {
          courseId,
          moduleId,
          taskId: task.id,
          resourceId: resource.id,
          data: { completed: true },
        };
        await updateProgress.mutate(progressData);
      }

      // Then open the URL
      if (resource.type === 'link' || resource.url) {
        const finalUrl = resource.type === 'link'
          ? resource.url.startsWith('http')
            ? resource.url
            : `http://${resource.url}`
          : resource.url;

        if (Platform.OS === 'web') {
          window.open(finalUrl, '_blank', 'noopener,noreferrer');
        } else {
          Linking.openURL(finalUrl);
        }
      } else {
        showToastMessage('error', t('student.task.resourcePanel.errors.resource-url-failed'));
      }
    }
  };

  return (
    <>
      <Pressable
        testID={`resource-item-${resource.id}`}
        onPress={handleResourcePress}
        p="$2"
        mx="$-2"
        borderRadius="$lg"
        sx={{
          ':hover': {
            bg: '$bgHover',
          },
        }}
      >
        <HStack alignItems="center" space="md">
          <ResourceThumbnail resource={resource} thumbnail={thumbnail} />

          <VStack flex={1} space="$0">
            <Text size="xs" color="$textHeading" numberOfLines={2}>
              {resource.title}
            </Text>
            {!!resource.citation && (
              <Text size="2xs" color="$textSubtle" numberOfLines={1}>
                {resource.citation}
              </Text>
            )}
          </VStack>

          <Menu
            placement="bottom right"
            trigger={({ ...triggerProps }) => (
              <Pressable
                {...triggerProps}
                onPress={(e) => {
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  triggerProps?.onPress?.(e);
                }}
                accessibilityLabel="Resource options"
                hitSlop={8}
                p="$1.5"
                borderRadius="$sm"
                sx={{ ':hover': { bg: '$bgHover' } }}
              >
                <Icon as={MoreVertical} size="sm" color="$textSubtle" />
              </Pressable>
            )}
          >
            {!!resource.citation && (
              <MenuItem
                key="copy-citation"
                textValue="Copy citation"
                onPress={() => {
                  // Call synchronously - do NOT await before clipboard call
                  copyToClipboard(resource.citation).then((success) => {
                    if (success) {
                      showToastMessage('success', t('common.clipboard.copied'));
                    } else {
                      showToastMessage('error', t('common.clipboard.copyFailed'));
                    }
                  });
                }}
              >
                <MenuItemLabel size="sm" ml="$2">
                  {t('student.task.resourcePanel.buttons.copy-citation')}
                </MenuItemLabel>
              </MenuItem>
            )}
          </Menu>
        </HStack>
      </Pressable>

      {showVideoModal && (
        <VideoModal isVisible={showVideoModal} onClose={() => setShowVideoModal(false)} resource={resource} />
      )}
    </>
  );
};

export default React.memo(ResourceItem);
